import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import { changeDirectSalesData, clearCampaignPlanShareUrl } from 'store/dealManagement/reducer';
import { getIsDirectSalesSessionReadOnly } from 'store/dealManagement/selectors';
import linkSvg from 'assets/icons/link.svg';
import IconButton from 'lib/IconButton';
import { disableDirectSalesSharedCampaign, enableDirectSalesSharedCampaign } from 'modules/api/directSalesCampaign';
import { cancelFunctions } from 'utils/cancelFunctions';
import Tooltip, { TooltipDirection, TooltipSize } from 'lib/Tooltip';
import { ShareCampaignPlanModal } from 'components/common/Modals/ShareCampaignPlanModal';
import { CAMPAIGN_PLAN_LINK_IS_DISABLED_MESSAGE } from 'components/common/Modals/ShareCampaignPlanModal/ShareCampaignPlanModal.types';
import { CampaignPlanConfig } from 'components/common/types/CampaignPlan.types';
import { Store } from 'components/common/types/Store.types';
import { getIsAnyDirectSalesLineHasPoi } from 'store/dealManagement/selectors/directSales';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import { useShareCampaignPlanModal } from 'components/common/Modals/ShareCampaignPlanModal/useShareCampaignPlanModal';
import { getMarketId } from 'store/publisher/selectors';

interface Props {
  isDisabled?: boolean;
}

export const ShareCampaignPlan: React.FC<Props> = ({ isDisabled = false }) => {
  const dispatch = useDispatch();
  const marketId = useSelector(getMarketId);
  const campaignId = useSelector(getDealId);
  const enabledForSharing = useSelector((state: Store) => state.dealManagement.directSales.enabledForSharing);
  const hasSelectedPois = useSelector(getIsAnyDirectSalesLineHasPoi);
  const isDirectSalesSessionLockedAndReadOnly = useSelector(getIsDirectSalesSessionReadOnly);

  const {
    setIsOpen,
    modalIsOpen,
    shareCampaignPlan,
    toggleCampaignPlan,
    campaignPlanConfig,
    isConfigLoading,
    configLoadError,
    shareCampaignPlan: { setParams: setShareParams },
    toggleCampaignPlan: { isChecked: isToggleChecked, setParams: setToggleParams },
    generateCampaignPlanLink,
    updateCampaignPlanConfig,
  } = useShareCampaignPlanModal();

  useEffect(() => {
    setToggleParams({ isChecked: enabledForSharing !== false, isDisabled: isDirectSalesSessionLockedAndReadOnly });

    if (!modalIsOpen) return;

    if (enabledForSharing === false) {
      setShareParams({
        error: '',
        disabledMessage: CAMPAIGN_PLAN_LINK_IS_DISABLED_MESSAGE,
      });
      return;
    }

    if (enabledForSharing === true) {
      setShareParams({ disabledMessage: '' });
    }
  }, [modalIsOpen, enabledForSharing, isDirectSalesSessionLockedAndReadOnly]);

  const applySharingState = (sharingEnabled: boolean | undefined): void => {
    setToggleParams({ isChecked: sharingEnabled !== false, error: '' });
    dispatch(changeDirectSalesData({ enabledForSharing: sharingEnabled }));

    if (sharingEnabled === false) {
      setShareParams({
        error: '',
        disabledMessage: CAMPAIGN_PLAN_LINK_IS_DISABLED_MESSAGE,
      });
      return;
    }

    setShareParams({ disabledMessage: '' });
  };

  const handleToggleSwitchChange = async (currentCampaignPlanConfig: CampaignPlanConfig): Promise<void> => {
    const previousEnabledForSharing = enabledForSharing;
    const nextEnabledForSharing = !isToggleChecked;

    applySharingState(nextEnabledForSharing);

    try {
      const toggleMethod = isToggleChecked ? disableDirectSalesSharedCampaign : enableDirectSalesSharedCampaign;

      const {
        campaign: { header },
      } = await toggleMethod(campaignId, marketId, cancelFunctions);

      applySharingState(header.enabledForSharing);

      if (!header.enabledForSharing) {
        setShareParams({ url: '', expiresAt: '' });
        dispatch(clearCampaignPlanShareUrl());
      }

      if (header.enabledForSharing && !previousEnabledForSharing) {
        await generateCampaignPlanLink(currentCampaignPlanConfig);
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        applySharingState(previousEnabledForSharing);
        return;
      }
    }
  };

  return (
    <>
      <Tooltip tooltip="Share campaign link" contentSize={TooltipSize.MAX} direction={TooltipDirection.BOTTOM}>
        <IconButton
          dataTestId="share-campaign-plan-button"
          icon={linkSvg}
          name="Share campaign link"
          handleOnClick={() => {
            setIsOpen(true);
          }}
          isDisabled={isDisabled}
        />
      </Tooltip>

      {modalIsOpen && (
        <ShareCampaignPlanModal
          shareCampaignPlan={shareCampaignPlan}
          toggleCampaignPlan={toggleCampaignPlan}
          hasSelectedPois={hasSelectedPois}
          initialCampaignPlanConfig={campaignPlanConfig}
          isConfigLoading={isConfigLoading}
          configLoadError={configLoadError}
          onClose={() => setIsOpen(false)}
          onToggleSwitchChange={handleToggleSwitchChange}
          onGenerateLink={generateCampaignPlanLink}
          onUpdateLink={updateCampaignPlanConfig}
        />
      )}
    </>
  );
};
